import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, shareReplay, switchMap, throwError } from 'rxjs';
import { apiPathname, isApiUrl, isPublicAuthUrl } from '@core/http/api-url';
import { AppConfigApiService } from '@core/services/api/app-config-api.service';
import { AppStateResetService } from '@core/state/app-state-reset.service';
import { AuthApiService } from '@features/auth/services/auth-api.service';
import { AuthStateService } from '@features/auth/services/auth-state.service';

/** Marks a request that has already been retried once after a 401. */
export const AUTH_RETRY = new HttpContextToken<boolean>(() => false);

let refreshInFlight: Observable<string> | null = null;
let sessionExpiredHandled = false;

/**
 * Turns a 401 from this app's API into either a recovered request or a login screen.
 *
 * The rule is exhaustive on purpose: **every** 401 that reaches here from an API
 * endpoint other than the public auth ones ends in one of two places - the request
 * replayed with a token that works, or the session cleared and the user sent to
 * `/auth`. There is no third branch that lets a 401 through untouched, because that
 * is what leaves someone sitting on a screen whose data never arrives and whose
 * every following request is refused too.
 *
 * Recovery is attempted once per request. What "once" means is carried on the
 * request itself ({@link AUTH_RETRY}), so a replay that is refused again is
 * recognised as the end of the session and not as another chance to refresh.
 */
@Injectable()
export class AuthExpiredInterceptor implements HttpInterceptor {
  private readonly authState = inject(AuthStateService);
  private readonly authApi = inject(AuthApiService);
  private readonly router = inject(Router);
  private readonly configApi = inject(AppConfigApiService);
  private readonly appStateReset = inject(AppStateResetService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (!this.isUnauthorized(error) || !this.isSessionRequest(request)) {
          return throwError(() => error);
        }

        return this.recover(request, next, error);
      }),
    );
  }

  /**
   * The single decision, with no way out that keeps the user where they are: either
   * this request can still be replayed with a working token, or the session is over.
   */
  private recover(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    error: unknown,
  ): Observable<HttpEvent<unknown>> {
    // The rotation itself was refused: there is nothing left to rotate with.
    if (this.isRefreshEndpoint(request.url)) {
      return this.handleSessionExpired(error);
    }

    // Already replayed once with a fresh token, and refused again.
    if (request.context.get(AUTH_RETRY)) {
      return this.handleSessionExpired(error);
    }

    const requestToken = this.extractBearerToken(request);
    const currentToken = this.authState.token();

    // Nothing to refresh with - the session was already cleared, or this request
    // left without a token at all. Late answers to requests fired just before a
    // logout land here, and so does a tab whose storage another tab emptied.
    if (!requestToken && !currentToken) {
      return this.handleSessionExpired(error);
    }

    // The request left carrying a token that has since been rotated by another
    // request's refresh. Replay it with the current one rather than refreshing again.
    if (requestToken && currentToken && requestToken !== currentToken) {
      return this.replay(request, next, currentToken);
    }

    return this.refreshAndRetry(request, next);
  }

  /**
   * Whether a 401 on this request means *our* session ended. The API's own origin
   * counts whole, so a document filed outside `/api/v1/` is covered; the endpoints
   * that answer before there is a session are not.
   */
  private isSessionRequest(request: HttpRequest<unknown>): boolean {
    return isApiUrl(request.url, this.configApi.getApiBaseUrl()) && !isPublicAuthUrl(request.url);
  }

  private refreshAndRetry(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return this.getRefreshedToken().pipe(
      // Only the refresh failing is caught here. Placed above the replay so that a
      // replayed request answering 500 is reported as the 500 it is, instead of
      // logging the user out over a server error.
      catchError((error: unknown) => this.handleSessionExpired(error)),
      switchMap((token) => this.replay(request, next, token)),
    );
  }

  private getRefreshedToken(): Observable<string> {
    if (!refreshInFlight) {
      refreshInFlight = this.authApi.refresh().pipe(
        map((result) => {
          this.authState.rotateToken(result);
          return result.token;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
        finalize(() => {
          refreshInFlight = null;
        }),
      );
    }

    return refreshInFlight;
  }

  /**
   * Sends the request again with the given token. A second 401 is the end of the
   * session: the token is as fresh as this app can make it and the API still refuses.
   */
  private replay(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    token: string,
  ): Observable<HttpEvent<unknown>> {
    return this.retryWithToken(request, next, token).pipe(
      catchError((error: unknown) =>
        this.isUnauthorized(error) ? this.handleSessionExpired(error) : throwError(() => error),
      ),
    );
  }

  private retryWithToken(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    token: string,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(
      request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
        context: request.context.set(AUTH_RETRY, true),
      }),
    );
  }

  /**
   * Ends the session and sends the user to the login screen, once however many
   * requests were refused together.
   *
   * The token goes first and directly: it is the one thing that has to be true when
   * this returns, and it does not wait on `AuthStateService` having registered its
   * teardown. Then the rest of the teardown a logout runs - whatever the previous
   * session left in the feature state services would otherwise still be on screen
   * behind the next person to log in on this tab.
   */
  private handleSessionExpired(error: unknown): Observable<never> {
    if (!sessionExpiredHandled) {
      sessionExpiredHandled = true;
      this.authState.reset();
      this.appStateReset.resetAll();
      void this.router.navigate(['/auth']);
    }

    return throwError(() => error);
  }

  private isUnauthorized(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 401;
  }

  private isRefreshEndpoint(url: string): boolean {
    return apiPathname(url).endsWith('/auth/refresh');
  }

  private extractBearerToken(request: HttpRequest<unknown>): string | null {
    const authorization = request.headers.get('Authorization');

    if (!authorization?.startsWith('Bearer ')) {
      return null;
    }

    return authorization.slice('Bearer '.length);
  }
}

/** Test helper — resets module-level refresh/session state between specs. */
export function resetAuthExpiredInterceptorState(): void {
  refreshInFlight = null;
  sessionExpiredHandled = false;
}
